
import { useEffect, useCallback } from 'react';
import { 
  updateLastActivityTimestamp, 
  hasSessionTimedOut, 
  clearSessionData,
  storeRedirectPath,
  hasValidSession,
  isTokenExpired
} from '@/utils/auth/sessionService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';

/**
 * Hook to handle session activity tracking and timeout management
 * This is now the central place for session management
 * @param timeoutMinutes Session timeout in minutes (default: 60)
 * @param onTimeout Optional callback to execute on timeout
 * @param disableTimeout Optional flag to temporarily disable timeout
 */
export const useSessionActivity = (
  timeoutMinutes = 60,
  onTimeout?: () => void,
  disableTimeout = false
) => {
  const location = useLocation();
  
  // Store current path for redirect after login
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    storeRedirectPath(currentPath);
  }, [location]);
  
  // Verify session and handle user activity
  useEffect(() => {
    // Initialize activity timestamp
    updateLastActivityTimestamp();
    
    // Function to handle user activity
    const handleUserActivity = () => {
      updateLastActivityTimestamp();
    };
    
    // Attach listeners for common user interactions
    window.addEventListener('mousedown', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('touchstart', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);
    
    // Logger function for debugging
    const logSessionCheck = (message: string, data?: any) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[SessionActivity] ${message}`, data || '');
      }
    };
    
    // Set up interval to check for session timeout
    const checkInterval = setInterval(async () => {
      // Skip timeout check if disableTimeout is true (for critical operations)
      if (disableTimeout) {
        logSessionCheck('Timeout check skipped (disabled)', { timeoutMinutes });
        return;
      }
      
      // Check if we have a valid session
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        logSessionCheck('No active session found');
        return;
      }
      
      // Check token expiration explicitly
      if (isTokenExpired(data.session.access_token)) {
        logSessionCheck('JWT token is expired, signing out');
        clearInterval(checkInterval);
        clearSessionData();
        await supabase.auth.signOut();
        toast.error(`Your session has expired. Please log in again.`);
        if (onTimeout) {
          onTimeout();
        }
        return;
      }
      
      // Check for inactivity timeout
      if (hasSessionTimedOut(timeoutMinutes)) {
        logSessionCheck(`Session timed out after ${timeoutMinutes} minutes of inactivity`);
        
        // Clear interval to prevent multiple executions
        clearInterval(checkInterval);
        
        // Clear session data first
        clearSessionData();
        
        // Attempt to sign out
        await supabase.auth.signOut();
        
        // Show a message
        toast.error(`Your session has expired after ${timeoutMinutes} minutes of inactivity. Please log in again.`);
        
        // Call timeout callback if provided
        if (onTimeout) {
          onTimeout();
        }
      } else {
        logSessionCheck('Session active, timeout check passed', { 
          timeoutMinutes,
          lastActivity: localStorage.getItem('lastActivityTimestamp')
        });
      }
    }, 60000); // Check every minute
    
    return () => {
      // Clean up
      window.removeEventListener('mousedown', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
      clearInterval(checkInterval);
      logSessionCheck('Session activity monitoring stopped');
    };
  }, [timeoutMinutes, onTimeout, disableTimeout]);
  
  // Verify session function for components to use
  const verifySession = useCallback(async (): Promise<boolean> => {
    try {
      return await hasValidSession();
    } catch (error) {
      console.error('Error verifying session:', error);
      return false;
    }
  }, []);
  
  return {
    updateActivity: updateLastActivityTimestamp,
    checkTimeout: () => hasSessionTimedOut(timeoutMinutes),
    verifySession
  };
};
